import { useRef, useState } from "react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { Copy, Download, QrCode } from "lucide-react";
import { Alert, Button, Card, CardBody, CardHeader, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";

export interface QrCodeCardProps {
  listingId: string;
  /** The permalink from `get` — null until the first mint. */
  qrUrl: string | null;
  /** Download filename stem — the SKU when there is one. */
  fileStem: string;
  /** Anything but `published` renders the unavailable-page notice. */
  visibility: "draft" | "published" | "archived";
  /** `store.catalog.manage` — minting is a write; without it the empty state
   *  explains instead of offering a dead button. */
  canManage: boolean;
}

/** Rendered pixel size of the preview; downloads export at 4× for print. */
const QR_SIZE = 168;
const QR_EXPORT_SIZE = 832;

/**
 * The product's QR permalink (spec 52), shown directly on the detail page —
 * preview, copy the URL, download as SVG or PNG for print. Minted lazily by
 * the inline button; once minted the code renders on every visit. The token
 * never changes for a listing, so a printed code stays valid across renames
 * and republishes; scanning opens the public product page with prices
 * revealed (ADR-0066), which is why the card says so out loud.
 */
export function QrCodeCard({
  listingId,
  qrUrl,
  fileStem,
  visibility,
  canManage,
}: QrCodeCardProps) {
  const svgWrapRef = useRef<HTMLDivElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const utils = trpc.useUtils();
  const ensureQr = trpc.storeCatalog.ensureQrToken.useMutation({
    // `get` carries qrToken/qrUrl — refetching it is what makes the code
    // appear in place after the first mint.
    onSuccess: () => void utils.storeCatalog.get.invalidate({ listingId }),
    onError: () => toast.error("Couldn't generate the QR code — try again."),
  });

  const copyUrl = async () => {
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the link text and copy manually.");
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSvg = () => {
    const svg = svgWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const source = new XMLSerializer().serializeToString(svg);
    downloadBlob(
      new Blob([source], { type: "image/svg+xml;charset=utf-8" }),
      `${fileStem}-qr.svg`,
    );
  };

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) downloadBlob(blob, `${fileStem}-qr.png`);
    }, "image/png");
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold text-ink">QR code</h2>
      </CardHeader>
      <CardBody>
        {!qrUrl ? (
          <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
            <p className="text-sm text-ink-2">No QR code yet</p>
            <p className="mt-1 text-xs text-muted">
              Scanning it opens the live product page — bundles, prices, variants and stock.
            </p>
            {canManage ? (
              <Button
                intent="primary"
                size="sm"
                className="mt-3"
                disabled={ensureQr.isPending}
                onClick={() => ensureQr.mutate({ listingId })}
              >
                <QrCode size={14} aria-hidden />
                Generate QR code
              </Button>
            ) : (
              <p className="mt-3 text-xs text-muted">
                Someone with catalog manage access can generate it.
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 pt-1">
            {/* White backing + padding = the quiet zone scanners need,
                whatever the app theme is doing. */}
            <div ref={svgWrapRef} className="rounded-md border border-line bg-white p-3">
              <QRCodeSVG value={qrUrl} size={QR_SIZE} marginSize={0} className="block" />
            </div>
            {/* Hidden print-resolution twin for the PNG export — same data, 4×. */}
            <div ref={canvasWrapRef} className="hidden" aria-hidden>
              <QRCodeCanvas value={qrUrl} size={QR_EXPORT_SIZE} marginSize={4} />
            </div>

            {/* The URL itself stays out of the UI (founder direction
                2026-08-14) — Copy link carries the share job without
                printing a token on screen. */}
            <div className="flex flex-wrap justify-center gap-2">
              <Button intent="secondary" size="sm" onClick={downloadSvg}>
                <Download size={14} aria-hidden />
                SVG
              </Button>
              <Button intent="secondary" size="sm" onClick={downloadPng}>
                <Download size={14} aria-hidden />
                PNG
              </Button>
              <Button intent="ghost" size="sm" onClick={() => void copyUrl()}>
                <Copy size={14} aria-hidden />
                {copied ? "Copied!" : "Copy link"}
              </Button>
            </div>

            {visibility !== "published" ? (
              <Alert
                intent="warn"
                description="This product isn't published — the QR page will say it's unavailable until you publish. The code itself stays valid."
              />
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
